import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { waitLeft, recordFailure, recordSuccess, waitMessage, FREE_TRIES } from '../lib/loginThrottle'
import { PRIVACY_URL, TERMS_URL, LICENCES_PATH } from '../lib/legal'

export default function Login() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [vessel, setVessel] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resetSent, setResetSent] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!email || !password) { setError('Please enter your email and password.'); return }
    // after 3 wrong passwords for this address: a growing wait, said plainly (audit-full F-9)
    const left = waitLeft(email)
    if (left) { setError(waitMessage(left)); return }
    setLoading(true); setError('')
    try {
      await signIn(email, password)
      recordSuccess(email)
      navigate('/dashboard')
    } catch (err) {
      const bad = /invalid login|invalid credentials|invalid password/i.test(err.message || 'invalid credentials')
      const f = bad ? recordFailure(email) : null
      setError((err.message || 'Invalid credentials. Please try again.') + (f && f.wait ? ' ' + waitMessage(f.wait) : f && f.n === FREE_TRIES - 1 ? ' One more wrong try and the next one waits a few seconds.' : ''))
    } finally { setLoading(false) }
  }

  async function handleReset() {
    if (!email) { setError('Enter your email address above first.'); return }
    setResetLoading(true); setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      // back to the site the reset was asked from (app.heycaptain.io, or a preview); must be in Supabase's allowed redirect URLs
      redirectTo: window.location.origin + '/reset-password'
    })
    setResetLoading(false)
    if (error) setError(error.message)
    else setResetSent(true)
  }

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const bgImage = hour < 12 ? '/Yacht_morning_1.png' : hour < 18 ? '/Yacht_afternoon_1.png' : '/Yacht_evening_1.png'

  return (
    <div style={{...s.page, backgroundImage:`url('${bgImage}')`}}>
      <div style={s.overlay} />

      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        .login-field input { width:100%; background:rgba(5,12,28,0.92); border:1px solid rgba(255,255,255,0.12); border-radius:10px; padding:12px 15px; color:#ffffff; font-size:13px; font-family:'DM Sans',sans-serif; outline:none; transition:all .2s; }
        .login-field input::placeholder { color:rgba(255,255,255,0.35); }
        .login-field input:focus { background:rgba(5,12,28,0.96); border-color:rgba(100,160,255,0.8); box-shadow:0 0 0 3px rgba(100,160,255,0.12); }
        .login-field label { display:block; font-size:11px; font-weight:500; color:rgba(255,255,255,0.7); letter-spacing:.12em; text-transform:uppercase; margin-bottom:7px; }
        .submit-btn:hover { background:rgba(212,176,106,1) !important; transform:translateY(-1px); box-shadow:0 8px 25px rgba(184,150,62,0.4) !important; }
        .submit-btn:active { transform:translateY(0); }
      `}</style>

      <div style={s.center}>
        <div style={s.cardWrap}>
          <div style={s.card}>
            <div style={s.anchorWrap}>
              <svg width="30" height="26" viewBox="0 0 32 28" fill="none" style={{flexShrink:0}}>
                <path d="M3 10 Q7.5 5 12 10 T21 10 T30 10" stroke="#2f6bff" strokeWidth="2.8" strokeLinecap="round"/>
                <path d="M3 18 Q7.5 13 12 18 T21 18 T30 18" stroke="#2f6bff" strokeWidth="2.8" strokeLinecap="round"/>
              </svg>
              <span style={s.wordmark}>HEYCAPTAIN</span>
            </div>

            <div style={s.greeting}>{greeting},</div>
            <div style={s.title}>Captain</div>
            <div style={s.sub}>Your yacht. Your crew. Your command.</div>
            <div style={s.divider} />

            <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:13}}>
              <div className="login-field">
                <label>Vessel Name</label>
                <input type="text" placeholder="e.g. M/Y Aurora" value={vessel} onChange={e=>setVessel(e.target.value)} />
              </div>
              <div className="login-field">
                <label>Email Address</label>
                <input type="text" placeholder="captain@vessel.com" value={email} onChange={e=>setEmail(e.target.value)} autoComplete="email" />
              </div>
              <div className="login-field">
                <label>Password</label>
                <input type="password" placeholder="Your password" value={password} onChange={e=>setPassword(e.target.value)} autoComplete="current-password" />
              </div>

              {error && <div style={{fontSize:11,color:'#f09595',background:'rgba(192,67,74,.15)',border:'0.5px solid rgba(192,67,74,.3)',borderRadius:8,padding:'8px 12px'}}>{error}</div>}

              <button type="submit" disabled={loading} className="submit-btn" style={s.btn}>
                {loading
                  ? <span style={{display:'flex',alignItems:'center',gap:8,justifyContent:'center'}}>
                      <span style={{width:14,height:14,border:'1.5px solid rgba(10,22,40,0.3)',borderTopColor:'#0a1628',borderRadius:'50%',display:'inline-block'}} />
                      Signing in...
                    </span>
                  : 'Sign in to dashboard'
                }
              </button>
            </form>

            {resetSent
              ? <div style={{textAlign:'center',fontSize:11,color:'#4caf82',marginTop:12}}>Password reset email sent — check your inbox.</div>
              : <button onClick={handleReset} disabled={resetLoading} style={{background:'none',border:'none',color:'rgba(255,195,50,1)',fontSize:11,cursor:'pointer',width:'100%',textAlign:'center',marginTop:12,fontFamily:'DM Sans,sans-serif',letterSpacing:'.03em'}}>
                  {resetLoading ? 'Sending...' : 'Forgot password?'}
                </button>
            }
            <div style={{textAlign:'center',fontSize:10.5,marginTop:16,fontFamily:'DM Sans,sans-serif',letterSpacing:'.03em',color:'rgba(255,255,255,0.45)'}}>
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{color:'rgba(255,255,255,0.6)',textDecoration:'none'}}>Privacy</a>
              {' \u00b7 '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{color:'rgba(255,255,255,0.6)',textDecoration:'none'}}>Terms</a>
              {' \u00b7 '}
              <a href={LICENCES_PATH} target="_blank" rel="noopener noreferrer" style={{color:'rgba(255,255,255,0.6)',textDecoration:'none'}}>Open-source licences</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const s = {
  page: {
    minHeight:'100vh',
    backgroundSize:'100%',
    backgroundPosition:'center 60%',
    display:'flex', alignItems:'center', justifyContent:'center',
    position:'relative', overflow:'hidden',
  },
  overlay: {
    position:'absolute', inset:0,
    background:'rgba(0,0,10,0.5)',
    zIndex:1,
  },
  center: {
    position:'relative', zIndex:15,
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  cardWrap: {
    borderRadius:20,
    padding:1.5,
    background:'rgba(234, 236, 244, 0.48)',
    boxShadow:'0 0 60px rgba(0,30,80,0.3), 0 30px 80px rgba(0,0,0,0.4)',
    animation:'fadeUp .6s ease forwards',
  },
  card: {
    width:378,
    background:'rgba(8,20,50,0.45)',
    backdropFilter:'blur(40px)',
    borderRadius:19,
    padding:'36px 32px',
  },
  anchorWrap: {
    display:'flex', alignItems:'center', justifyContent:'center', gap:10,
    marginBottom:16,
  },
  wordmark: {
    fontFamily:'DM Sans, sans-serif',
    fontSize:18, fontWeight:700,
    color:'#ffffff', letterSpacing:'.06em',
  },
  greeting: {
    fontFamily:'Cormorant Garamond, serif',
    fontSize:24, fontWeight:300, fontStyle:'italic',
    color:'rgba(255,255,255,0.95)',
    textAlign:'center', letterSpacing:'.02em',
  },
  title: {
    fontFamily:'Cormorant Garamond, serif',
    fontSize:52, fontWeight:500,
    color:'#ffffff',
    textAlign:'center', letterSpacing:'.02em',
    lineHeight:1.05, marginBottom:7,
  },
  sub: {
    fontFamily:'DM Sans, sans-serif',
    fontSize:14, color:'rgba(255,255,255,0.7)',
    textAlign:'center', letterSpacing:'.05em',
    marginBottom:18,
  },
  divider: {
    width:36, height:1,
    background:'linear-gradient(90deg, transparent, rgba(184,150,62,0.8), transparent)',
    margin:'0 auto 22px',
  },
  btn: {
    width:'100%', padding:'13px',
    borderRadius:10, border:'none',
    background:'rgba(184,150,62,1)',
    color:'#0a1628',
    fontFamily:'DM Sans, sans-serif',
    fontSize:13, fontWeight:600,
    cursor:'pointer', marginTop:6,
    transition:'all .2s',
    letterSpacing:'.03em',
    boxShadow:'0 4px 20px rgba(184,150,62,0.3)',
  },
}